import React, { useEffect, useState } from 'react';
import { Button, FormGroup, Input, Label } from 'reactstrap';
import queryString from 'query-string';
import { programRequestsApi } from '../api/programRequestsApi';
import { InstitutionField } from '../shared/InstitutionField';
import { ProgramSelector } from '../shared/ProgramSelector';
import {
    AdditionalCommentsField,
    commentsLengthError,
} from '../shared/AdditionalCommentsField';
import {
    TeachoutField,
    teachoutRequiredError,
    AllStudentsTaughtOutField,
    allStudentsTaughtOutError,
} from '../shared/TeachoutField';
import { createChangeStatusModel } from './changeStatusModel';
import { createChangeStatusFlow } from './createChangeStatusFlow';

const STATUS_LABELS = {
    0: 'New',
    1: 'Active',
    3: 'Suspended',
    4: 'Eliminated',
    5: 'Merged',
    6: 'Deleted',
    7: 'Consolidated',
};

const ACTION_OPTIONS = [
    { value: 'Eliminating', label: 'Set this program to Eliminated' },
    { value: 'Suspending', label: 'Set this program to Suspended' },
    { value: 'Reinstating', label: 'Set this program to Active' },
];


const ACTION_TARGET_SUBTYPES = {
    Eliminating: 'Eliminated',
    Reinstating: 'Active',
};

function subtypeMatchesCurrentStatus(
    form,
    program,
    teachoutFlag = form.teachoutFlag,
    allStudentsTaughtOut = form.allStudentsTaughtOut
) {
    if (!form.action || !program) return false;

    const currentSubtype =
        program.programStatusTypeDesc ??
        program.programStatusType ??
        STATUS_LABELS[Number(program.programStatusId)];
    const selectedSubtype = form.action === 'Suspending'
        ? (form.suspensionType === 'Suspended' &&
            (teachoutFlag === false || allStudentsTaughtOut === true)
            ? 'Eliminated'
            : form.suspensionType)
        : ACTION_TARGET_SUBTYPES[form.action];

    return (
        selectedSubtype != null &&
        String(currentSubtype).toLowerCase() === selectedSubtype.toLowerCase()
    );
}

function hasValidationErrors(
    form,
    selectedProgram,
    maximumCommentsLength,
    teachoutFlag,
    allStudentsTaughtOut
) {
    if (!form.institutionId || !form.programId || !form.action) return true;
    if (commentsLengthError(form.comments, maximumCommentsLength)) return true;
    if (form.action === 'Suspending' && !form.suspensionType) return true;
    if (subtypeMatchesCurrentStatus(
        form,
        selectedProgram,
        teachoutFlag,
        allStudentsTaughtOut
    )) return true;
    if (form.action === 'Eliminating') {
        if (teachoutRequiredError(teachoutFlag)) return true;
        if (teachoutFlag === true) {
            return Boolean(allStudentsTaughtOutError(allStudentsTaughtOut));
        }
        return false;
    }
    if (form.action === 'Suspending') {
        if (form.suspensionType !== 'Suspended') return false;
        if (teachoutRequiredError(teachoutFlag)) return true;
        if (teachoutFlag === true) {
            return Boolean(allStudentsTaughtOutError(allStudentsTaughtOut));
        }
        return false;
    }
    return false;
}

const Radio = ({ id, name, checked, onChange, className = 'form-check', children }) => (
    <div className={className}>
        <input
            className="form-check-input"
            type="radio"
            id={id}
            name={name}
            checked={checked}
            onChange={onChange}
        />
        <label className="form-check-label" htmlFor={id}>
            {children}
        </label>
    </div>
);

export const ChangeStatusForm = ({
    onBack,
    onSuccess,
    institutionOptions: parentInstitutionOptions = [],
    programsByInstitution = {},
    maximumAdditionalCommentsLength,
    initialModel,
    submitFlow = createChangeStatusFlow,
    mode = 'create',
    requestDetails,
}) => {
    const [form, setForm] = useState(
        () => initialModel ?? createChangeStatusModel()
    );
    const [institutionOptions, setInstitutionOptions] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [loadingPrograms, setLoadingPrograms] = useState(false);
    const [initializingEdit, setInitializingEdit] = useState(mode === 'edit');
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    useEffect(() => {
        if (parentInstitutionOptions.length > 0) {
            setInstitutionOptions(parentInstitutionOptions);
        } else {
            programRequestsApi.getInstitutions().then(res => {
                setInstitutionOptions(
                    (res ?? []).map(x => ({ value: x.label, label: x.value }))
                );
            });
        }
    }, []);

    useEffect(() => {
        if (institutionOptions.length === 1 && form.institutionId === null) {
            setForm(current => ({
                ...current,
                institutionId: institutionOptions[0].value,
            }));
        }
    }, [institutionOptions]);

    useEffect(() => {
        if (!form.institutionId) {
            setPrograms([]);
            setForm(current => ({
                ...current,
                programId: null,
                action: null,
                suspensionType: null,
                teachoutFlag: null,
                allStudentsTaughtOut: null,
            }));
            setInitializingEdit(false);
            return;
        }

        if (mode !== 'edit') {
            setForm(current => ({
                ...current,
                programId: null,
                action: null,
                suspensionType: null,
                teachoutFlag: null,
                allStudentsTaughtOut: null,
            }));
        }

        const cached = programsByInstitution[form.institutionId];
        if (Array.isArray(cached) && cached.length > 0) {
            setPrograms(cached);
            setInitializingEdit(false);
            return;
        }

        setLoadingPrograms(true);
        const query = queryString.stringify(
            { institutions: form.institutionId },
            { arrayFormat: 'separator', skipEmptyString: true }
        );
        programRequestsApi
            .getProgramsForInstitution(query)
            .then(res => setPrograms(res?.programs?.flat() ?? []))
            .finally(() => {
                setLoadingPrograms(false);
                setInitializingEdit(false);
            });
    }, [form.institutionId, mode]);

    const setField = (key, value) => {
        setForm(current => ({ ...current, [key]: value }));
    };

    const handleProgramChange = programId => {
        setForm(current => ({
            ...current,
            programId,
            action: null,
            suspensionType: null,
            teachoutFlag: null,
            allStudentsTaughtOut: null,
        }));
    };

    const handleActionChange = action => {
        setForm(current => ({
            ...current,
            action,
            suspensionType: null,
            teachoutFlag: null,
            allStudentsTaughtOut: null,
        }));
    };

    const handleTeachoutFlagChange = teachoutFlag => {
        setForm(current => ({
            ...current,
            teachoutFlag,
            allStudentsTaughtOut: teachoutFlag === true ? current.allStudentsTaughtOut : null,
        }));
    };

    const handleSuspensionTypeChange = suspensionType => {
        setForm(current => ({
            ...current,
            suspensionType,
            teachoutFlag:
                suspensionType === 'Suspended' ? current.teachoutFlag : null,
            allStudentsTaughtOut:
                suspensionType === 'Suspended' ? current.allStudentsTaughtOut : null,
        }));
    };

    const selectedProgram =
        programs.find(program => program.programId === form.programId) ?? null;
    // A prior Merge or Consolidate persists this answer on the program.  Do not
    // ask it again; a prior Yes still needs the current completion answer.
    const savedTeachoutFlag = selectedProgram?.teachOutFlag ??
        selectedProgram?.teachoutFlag ?? null;
    const effectiveTeachoutFlag = savedTeachoutFlag === null
        ? form.teachoutFlag
        : savedTeachoutFlag;
    const savedAllStudentsTaughtOut = selectedProgram?.allStudentsTaughtOut ??
        selectedProgram?.AllStudentsTaughtOut ?? null;
    // A prior No is not terminal; ask again so the request can record that
    // teach-out is now complete.  Only a prior Yes suppresses this question.
    const effectiveAllStudentsTaughtOut = savedAllStudentsTaughtOut === true
        ? true
        : form.allStudentsTaughtOut;
    const currentStatus = selectedProgram
        ? selectedProgram.programStatusDesc ??
        STATUS_LABELS[Number(selectedProgram.programStatusId)] ??
        'Unknown'
        : null;
    const selectedSubtypeMatchesCurrentStatus = subtypeMatchesCurrentStatus(
        form,
        selectedProgram,
        effectiveTeachoutFlag,
        effectiveAllStudentsTaughtOut
    );
    const eliminatesConsolidatedProgram =
        form.action === 'Eliminating' &&
        Number(selectedProgram?.programStatusId) === 3 &&
        String(selectedProgram?.statusType ?? '').toLowerCase() === 'consolidated' &&
        effectiveTeachoutFlag === true &&
        effectiveAllStudentsTaughtOut === true;
    const retainedStatusType = ['merged', 'consolidated'].includes(
        String(selectedProgram?.statusType ?? '').toLowerCase()
    )
        ? selectedProgram.statusType
        : null;
    const statusTypeLabel = defaultType => retainedStatusType ?? defaultType;

    const handleSubmit = () => {
        setSubmitted(true);
        setSubmitError(null);
        if (hasValidationErrors(
            form,
            selectedProgram,
            maximumAdditionalCommentsLength,
            effectiveTeachoutFlag,
            effectiveAllStudentsTaughtOut
        )) return;

        setSubmitting(true);
        const onError = error => {
            const message =
                error?.response?.data?.message ??
                'An unexpected error occurred. Please try again.';
            setSubmitError(message);
            setSubmitting(false);
        };

        submitFlow({
            form: {
                ...form,
                teachoutFlag: effectiveTeachoutFlag,
                allStudentsTaughtOut: effectiveAllStudentsTaughtOut,
            },
            details: requestDetails,
            onError,
        }).then(result => {
            setSubmitting(false);
            if (!result) return;
            onSuccess();
        });
    };

    if (mode === 'edit' && initializingEdit) {
        return (
            <p className="text-muted small text-center py-4 mb-0">
                Loading request details…
            </p>
        );
    }

    return (
        <div>
            {mode !== 'edit' && (
                <button type="button" className="btn btn-link p-0 mb-3" onClick={onBack}>
                    ← Back
                </button>
            )}

            <InstitutionField
                institutionOptions={institutionOptions}
                value={form.institutionId}
                onChange={value => setField('institutionId', value)}
            />
            {submitted && !form.institutionId && (
                <p className="text-danger mt-1">Institution is required.</p>
            )}

            {loadingPrograms && (
                <p className="text-muted small mb-2">Loading programs…</p>
            )}

            <ProgramSelector
                inputId="change-status-program"
                programs={programs}
                value={form.programId}
                onChange={handleProgramChange}
            />
            {submitted && !form.programId && (
                <p className="text-danger mt-1">Program is required.</p>
            )}

            {selectedProgram && (
                <FormGroup>
                    <Label className="d-block mb-1">Current Status</Label>
                    <span className="badge badge-secondary px-3 py-2">{currentStatus}</span>
                </FormGroup>
            )}

            <FormGroup>
                <Label className="d-block">
                    Action <sup className="text-danger">*</sup>
                </Label>
                {ACTION_OPTIONS.map(option => (
                    <Radio
                        key={option.value}
                        id={`change-status-action-${option.value.toLowerCase()}`}
                        name="change-status-action"
                        checked={form.action === option.value}
                        onChange={() => handleActionChange(option.value)}
                    >
                        {option.label}
                    </Radio>
                ))}
                {submitted && !form.action && (
                    <p className="text-danger mt-1">Action is required.</p>
                )}
                {submitted && selectedSubtypeMatchesCurrentStatus && (
                    <p className="text-danger mt-1">
                        The selected status subtype must be different from the
                        program's current status subtype.
                    </p>
                )}
            </FormGroup>

            {form.action === 'Eliminating' && (
                <div className="border-top pt-3">
                    {savedTeachoutFlag === null && (
                        <TeachoutField
                            id="change-status-teachout"
                            value={form.teachoutFlag}
                            onChange={handleTeachoutFlagChange}
                            submitted={submitted}
                        />
                    )}
                    {effectiveTeachoutFlag === true && savedAllStudentsTaughtOut !== true && (
                        <AllStudentsTaughtOutField
                            id="change-status-all-taught-out"
                            value={form.allStudentsTaughtOut}
                            onChange={value => setField('allStudentsTaughtOut', value)}
                            submitted={submitted}
                        />
                    )}
                    <p className="small text-muted">
                        {effectiveTeachoutFlag === false
                            ? `Will set: Status = Eliminated, Type = ${statusTypeLabel('Eliminated')}, Teachout = No`
                            : effectiveTeachoutFlag === true
                                ? (effectiveAllStudentsTaughtOut === true
                                    ? (eliminatesConsolidatedProgram
                                        ? 'Will set: Status = Eliminated, Type = Consolidated, Teachout = Yes'
                                        : `Will set: Status = Eliminated, Type = ${statusTypeLabel('Eliminated')}, Teachout = Yes`)
                                    : form.allStudentsTaughtOut === false
                                        ? `Will set: Status = Suspended, Type = ${statusTypeLabel('Suspended')}, Teachout = Yes`
                                        : 'Will set after you indicate whether all students are taught out.')
                                : 'Will set after you indicate whether a teach-out is required.'}
                    </p>
                </div>
            )}

            {form.action === 'Suspending' && (
                <div className="border-top pt-3">
                    <FormGroup>
                        <Label className="d-block">
                            Will the program possibly be reinstated later? <sup className="text-danger">*</sup>
                        </Label>
                        <Radio
                            id="change-status-suspension-reinstated-yes"
                            name="change-status-suspension-reinstated"
                            checked={form.suspensionType === 'Paused'}
                            onChange={() => handleSuspensionTypeChange('Paused')}
                            className="form-check form-check-inline"
                        >
                            Yes
                        </Radio>
                        <Radio
                            id="change-status-suspension-reinstated-no"
                            name="change-status-suspension-reinstated"
                            checked={form.suspensionType === 'Suspended'}
                            onChange={() => handleSuspensionTypeChange('Suspended')}
                            className="form-check form-check-inline"
                        >
                            No
                        </Radio>
                        {submitted && !form.suspensionType && (
                            <p className="text-danger mt-1">
                                Please indicate whether the program may be reinstated later.
                            </p>
                        )}
                    </FormGroup>
                    {form.suspensionType === 'Suspended' && (
                        <>
                            {savedTeachoutFlag === null && (
                                <TeachoutField
                                    id="change-status-suspension-teachout"
                                    value={form.teachoutFlag}
                                    onChange={handleTeachoutFlagChange}
                                    submitted={submitted}
                                />
                            )}
                            {effectiveTeachoutFlag === true && savedAllStudentsTaughtOut !== true && (
                                <AllStudentsTaughtOutField
                                    id="change-status-suspension-all-taught-out"
                                    value={form.allStudentsTaughtOut}
                                    onChange={value => setField('allStudentsTaughtOut', value)}
                                    submitted={submitted}
                                />
                            )}
                        </>
                    )}
                    <p className="small text-muted">
                        {form.suspensionType === 'Paused'
                            ? `Will set: Status = Suspended, Type = ${statusTypeLabel('Paused')}`
                            : form.suspensionType === 'Suspended'
                                ? (effectiveTeachoutFlag === false
                                    ? `Will set: Status = Eliminated, Type = ${statusTypeLabel('Eliminated')}, Teachout = No`
                                    : effectiveTeachoutFlag === true
                                        ? (effectiveAllStudentsTaughtOut === true
                                            ? `Will set: Status = Eliminated, Type = ${statusTypeLabel('Eliminated')}, Teachout = Yes`
                                            : form.allStudentsTaughtOut === false
                                                ? `Will set: Status = Suspended, Type = ${statusTypeLabel('Suspended')}, Teachout = Yes`
                                                : 'Will set after you indicate whether all students are taught out.')
                                        : 'Will set after you indicate whether a teach-out is required.')
                                : 'Will set after you indicate whether the program may be reinstated later.'}
                    </p>
                </div>
            )}

            {form.action === 'Reinstating' && (
                <div className="border-top pt-3">
                    <p className="small text-muted">
                        Will set: Status = Active, Type = Active
                    </p>
                </div>
            )}

            <AdditionalCommentsField
                id="change-status-comments"
                value={form.comments}
                onChange={value => setField('comments', value)}
                maximumLength={maximumAdditionalCommentsLength}
                submitted={submitted}
            />

            {submitError && (
                <div className="alert alert-danger mt-3" role="alert">
                    {submitError}
                </div>
            )}

            <div className="d-flex justify-content-end mt-4">
                <Button
                    color="secondary"
                    className="mr-2"
                    onClick={onBack}
                    disabled={submitting}
                >
                    Cancel
                </Button>
                <Button color="primary" onClick={handleSubmit} disabled={submitting}>
                    {submitting
                        ? mode === 'edit' ? 'Saving…' : 'Submitting…'
                        : mode === 'edit' ? 'Save Changes' : 'Submit Request'}
                </Button>
            </div>
        </div>
    );
};
